import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { Input } from '@redpanda/ui';
import FormFieldWithError from '../../components/forms/FormFieldWithError';
import { toast } from 'react-hot-toast';

interface RegisterFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { register: registerCustomer } = useCustomerAuth();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setSubmitting(true);
      await registerCustomer({
        name: data.name,
        email: data.email,
        password: data.password,
        phone: data.phone,
      });
      toast.success(t('pages:booking.register.registerSuccess'));
      navigate(`/book/${storeId}`);
    } catch (error: any) {
      toast.error(error.message || t('pages:booking.register.registerFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-md">
        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.register.backToHome')}</span>
        </button>

        {/* 註冊卡片 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.register.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.register.subtitle')}
            </p>
          </div>

          {/* 註冊表單 */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email */}
            <FormFieldWithError
              label={t('pages:booking.register.email')}
              required
              error={errors.email?.message}
              htmlFor="email"
            >
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: t('translation:validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('translation:validation.emailInvalid'),
                  },
                })}
                variant={errors.email ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 姓名 */}
            <FormFieldWithError
              label={t('pages:booking.register.name')}
              required
              error={errors.name?.message}
              htmlFor="name"
            >
              <Input
                id="name"
                type="text"
                {...register('name', {
                  required: t('translation:validation.nameRequired'),
                  minLength: { value: 2, message: t('pages:booking.register.nameMinLength') },
                })}
                variant={errors.name ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 電話 */}
            <FormFieldWithError
              label={t('pages:booking.register.phone')}
              required={true}
              error={errors.phone?.message}
              htmlFor="phone"
            >
              <Input
                id="phone"
                type="tel"
                {...register('phone', {
                  required: t('translation:validation.phoneRequired'),
                  pattern: {
                    value: /^[0-9\-\s\+\(\)]+$/,
                    message: t('translation:validation.phoneInvalid'),
                  },
                })}
                variant={errors.phone ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 密碼 */}
            <FormFieldWithError
              label={t('pages:booking.register.password')}
              required
              error={errors.password?.message}
              htmlFor="password"
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password', {
                  required: t('translation:validation.passwordRequired'),
                  minLength: { value: 6, message: t('translation:validation.passwordMinLength') },
                })}
                variant={errors.password ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 確認密碼 */}
            <FormFieldWithError
              label={t('pages:booking.register.confirmPassword')}
              required
              error={errors.confirmPassword?.message}
              htmlFor="confirmPassword"
            >
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword', {
                  required: t('pages:booking.register.confirmPasswordRequired'),
                  validate: (value) =>
                    value === password || t('translation:validation.passwordMismatch'),
                })}
                variant={errors.confirmPassword ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 註冊按鈕 */}
            <button
              type="submit"
              disabled={submitting}
              className={`
                w-full px-6 py-3 rounded-lg font-bold text-white transition-all duration-200
                ${
                  submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {submitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('pages:booking.register.registering')}</span>
                </span>
              ) : (
                t('pages:booking.register.registerButton')
              )}
            </button>
          </form>

          {/* 登入連結 */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.register.hasAccount')}{' '}
              <Link
                to={`/book/${storeId}/login`}
                className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-semibold transition-colors"
              >
                {t('pages:booking.register.loginNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
